import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Col,
  Row,
  Card,
  Modal,
  Button,
  Dropdown,
  Table,
} from "react-bootstrap";

import swal from "sweetalert";
import { Link } from "react-router-dom";
import { EquipmentForm } from "./EquipmentForm";
import EquipmentItem from "./EquipmentItem";

import { db } from "../../../firebase";
import {
  orderBy,
  where,
  query,
  collection,
  and,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from "firebase/firestore";

import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import { usePagination } from "../../../hook/usePagination";

const EquipmentList = (props) => {
  const { currentUser } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [edit, setEdit] = useState({});
  const [searchItem, setSearchItem] = useState("");
  const [searchField, setSearchField] = useState("serialNumber"); // default

  const [touchedFields, setTouchedFields] = useState(new Set());
  const searchRef = useRef(null);

  const [showCreateSetModal, setShowCreateSetModal] = useState(false);
  const [newSetId, setNewSetId] = useState("");
  const [newSetBaseLocation, setNewSetBaseLocation] = useState("");
  const [newSetCurrentLocation, setNewSetCurrentLocation] = useState("");

  const [setSearch, setSetSearch] = useState("");
  const [selectedSet, setSelectedSet] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveToLocation, setMoveToLocation] = useState("");
  const [moveNote, setMoveNote] = useState("");

  // IMPORTANT: this is only for forcing React to re-render/recreate query objects.
  // We DON'T change usePagination.
  const [logRefreshKey, setLogRefreshKey] = useState(0);

  // ✅ IMPORTANT: separate key for sleepSets pagination stability (fix Prev bug)
  const [setListRefreshKey, setSetListRefreshKey] = useState(0);

  const inputRef = useRef({
    serialNumber: null,
    deviceName: null,
    model: null,
    description: null,
    location: null,
    lotNumber: null,
    qty: null,
    status: null,
    incomingDate: null,
    note: null,
    base: null,
    set: null,
  });

  // -----------------------------
  // Helpers
  // -----------------------------
  const formatMovedAt = (movedAt) => {
    try {
      if (!movedAt) return "-";
      if (typeof movedAt.toDate === "function") {
        return movedAt.toDate().toLocaleString();
      }
      if (movedAt instanceof Date) return movedAt.toLocaleString();
      if (movedAt && typeof movedAt.seconds === "number") {
        return new Date(movedAt.seconds * 1000).toLocaleString();
      }
      const d = new Date(movedAt);
      return isNaN(d.getTime()) ? "-" : d.toLocaleString();
    } catch (e) {
      return "-";
    }
  };

  // -----------------------------
  // Equipments List (search + pagination)
  // -----------------------------
  const itemPerPage = 20;
  let QueryFilterConstraint = [];

  let queryRef = query(
    collection(db, "equipments"),
    orderBy("status", "desc"),
    orderBy("serialNumber", "asc"),
  );

  if (searchItem.trim() !== "") {
    QueryFilterConstraint.push(
      where(searchField, ">=", searchItem),
      where(searchField, "<=", searchItem + "\uf8ff"),
    );

    queryRef = query(
      collection(db, "equipments"),
      and(...QueryFilterConstraint),
      orderBy("status", "desc"),
      orderBy(searchField, "asc"),
    );
  }

  const {
    items: equipmentsList,
    isStart,
    isEnd,
    getPrev,
    getNext,
    totalItems,
    totalPages,
    currentPage,
    goToPage,
  } = usePagination(queryRef, { limit: itemPerPage });

  // -----------------------------
  // Sleep Sets List (search + pagination) ✅ LIMIT=5 (NO priority field)
  // - Fix Prev bug: memoize query + refresh key on filter change
  // -----------------------------
  const setPerPage = 5;
  const setSearchUpper = (setSearch || "").trim().toUpperCase();

  // When setSearch changes, we force a new query object ONCE and optionally return to page 1
  useEffect(() => {
    setSetListRefreshKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchUpper]);

  const setQueryRef = useMemo(() => {
    // Default: order by setId only (stable pagination)
    let ref = query(collection(db, "sleepSets"), orderBy("setId", "asc"));

    // Search: prefix match on setId (still orderBy setId first)
    if (setSearchUpper) {
      ref = query(
        collection(db, "sleepSets"),
        where("setId", ">=", setSearchUpper),
        where("setId", "<=", setSearchUpper + "\uf8ff"),
        orderBy("setId", "asc"),
      );
    }

    return ref;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchUpper, setListRefreshKey]);

  const {
    items: sleepSetsList,
    isStart: setIsStart,
    isEnd: setIsEnd,
    getPrev: getSetPrev,
    getNext: getSetNext,
    totalPages: setTotalPages,
    currentPage: setCurrentPage,
    goToPage: goToSetPage,
  } = usePagination(setQueryRef, { limit: setPerPage });

  // Optional: go back to page 1 when searching
  useEffect(() => {
    if (typeof goToSetPage === "function") {
      goToSetPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchUpper]);

  // -----------------------------
  // Logs (GLOBAL)
  // -----------------------------
  const logsPerPage = 10;

  // ✅ IMPORTANT: derive set filter from BOTH:
  // 1) main search box when dropdown = "set"
  // 2) setSearch box
  const setFilterFromSetBox = (setSearch || "").trim().toUpperCase();
  const setFilterFromMainBox =
    searchField === "set" ? (searchItem || "").trim().toUpperCase() : "";

  const setFilter = setFilterFromMainBox || setFilterFromSetBox;

  // ✅ If user uses main box as "set", don't also apply eqQ to other fields
  const eqQ = searchField === "set" ? "" : (searchItem || "").trim();

  // ✅ Force rebuild key when filters change (helps pagination hooks that cache cursors)
  useEffect(() => {
    setLogRefreshKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilter, eqQ, searchField]);

  // LEFT: Sleep Set Logs (memoized so query changes are detected)
  const setLogsRef = useMemo(() => {
    let ref = query(
      collection(db, "sleepSetMoveLogs"),
      orderBy("movedAtClient", "desc"),
    );

    if (setFilter) {
      ref = query(
        collection(db, "sleepSetMoveLogs"),
        where("set", ">=", setFilter),
        where("set", "<=", setFilter + "\uf8ff"),
        orderBy("set", "asc"),
        orderBy("movedAtClient", "desc"),
      );
    }

    return ref;
  }, [setFilter, logRefreshKey]);

  const {
    items: sleepSetLogs,
    isStart: setLogIsStart,
    isEnd: setLogIsEnd,
    getPrev: getSetLogPrev,
    getNext: getSetLogNext,
    totalPages: setLogTotalPages,
    currentPage: setLogCurrentPage,
  } = usePagination(setLogsRef, { limit: logsPerPage });

  // RIGHT: Equipment Logs (memoized)
  const eqLogsRef = useMemo(() => {
    let ref = query(
      collection(db, "equipmentMoveLogs"),
      orderBy("movedAtClient", "desc"),
    );

    if (eqQ) {
      // Search by selected field
      ref = query(
        collection(db, "equipmentMoveLogs"),
        where(searchField, ">=", eqQ),
        where(searchField, "<=", eqQ + "\uf8ff"),
        orderBy(searchField, "asc"),
        orderBy("movedAtClient", "desc"),
      );
    } else if (setFilter) {
      // Filter by set (from either box)
      ref = query(
        collection(db, "equipmentMoveLogs"),
        where("set", ">=", setFilter),
        where("set", "<=", setFilter + "\uf8ff"),
        orderBy("set", "asc"),
        orderBy("movedAtClient", "desc"),
      );
    }

    return ref;
  }, [eqQ, searchField, setFilter, logRefreshKey]);

  const {
    items: equipmentLogs,
    isStart: eqLogIsStart,
    isEnd: eqLogIsEnd,
    getPrev: getEqLogPrev,
    getNext: getEqLogNext,
    totalPages: eqLogTotalPages,
    currentPage: eqLogCurrentPage,
  } = usePagination(eqLogsRef, { limit: logsPerPage });

  // -----------------------------
  // Modals & Actions
  // -----------------------------
  const openMoveModal = (setDoc) => {
    setSelectedSet(setDoc);
    setMoveToLocation(setDoc.currentLocation || "");
    setMoveNote("");
    setShowMoveModal(true);
  };

  const closeMoveModal = () => {
    setShowMoveModal(false);
    setSelectedSet(null);
    setMoveToLocation("");
    setMoveNote("");
  };

  const moveSleepSet = async () => {
    const setId = selectedSet && selectedSet.id; // docId A/B/C
    if (!setId) return;

    const toLocation = (moveToLocation || "").trim();
    if (!toLocation) {
      swal("Please enter location", { icon: "warning" });
      return;
    }

    const setRef = doc(db, "sleepSets", setId);

    try {
      // create subcollection log ref first to get moveLogId
      const logRef = doc(collection(db, "sleepSets", setId, "moveLogs"));
      const moveLogId = logRef.id;

      // use a client date for immediate ordering/display
      const movedAtClient = new Date();

      // transaction: update sleepSet + create set log + create GLOBAL set log
      let setFromLocation = "";

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(setRef);
        if (!snap.exists()) throw new Error("Sleep set not found");

        const data = snap.data();
        setFromLocation = data.currentLocation || "";

        tx.update(setRef, {
          currentLocation: toLocation,
          updatedAt: serverTimestamp(),
          updatedBy: (currentUser && currentUser.displayName) || null,
          lastMoveLogId: moveLogId,
        });

        // subcollection set log
        tx.set(logRef, {
          set: data.setId || setId, // sleepSets schema uses setId
          fromLocation: setFromLocation,
          toLocation: toLocation,
          note: moveNote || "",
          movedAt: serverTimestamp(), // real server time
          movedAtClient: movedAtClient, // instant UI sort
          movedBy: (currentUser && currentUser.displayName) || null,
          reason: "MOVE_SET",
          setMoveLogId: moveLogId,
        });

        // GLOBAL set log
        const globalSetLogRef = doc(collection(db, "sleepSetMoveLogs"));
        tx.set(globalSetLogRef, {
          set: data.setId || setId,
          fromLocation: setFromLocation,
          toLocation: toLocation,
          note: moveNote || "",
          movedAt: serverTimestamp(),
          movedAtClient: movedAtClient,
          movedBy: (currentUser && currentUser.displayName) || null,
          reason: "MOVE_SET",
          setMoveLogId: moveLogId,
        });
      });

      // query equipments by set
      const eqSnap = await getDocs(
        query(collection(db, "equipments"), where("set", "==", setId)),
      );

      // batch update equipments + logs
      const docs = eqSnap.docs;
      const CHUNK = 150; // 150 * 3 writes = 450 safe

      for (let i = 0; i < docs.length; i += CHUNK) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + CHUNK);

        chunk.forEach((d) => {
          const eq = d.data();
          const equipmentId = d.id;

          const fromLocationEq = eq.location || "";
          const baseEq = eq.base || "";

          // update equipment location
          batch.update(doc(db, "equipments", equipmentId), {
            location: toLocation,
            updatedAt: serverTimestamp(),
            updatedBy: (currentUser && currentUser.displayName) || null,
            lastSetMoveLogId: moveLogId,
          });

          // equipment subcollection log
          const eqLogRef = doc(
            collection(db, "equipments", equipmentId, "moveLogs"),
          );
          batch.set(eqLogRef, {
            movedAt: serverTimestamp(),
            movedAtClient: movedAtClient,
            movedBy: (currentUser && currentUser.displayName) || null,
            note: moveNote || "",
            reason: "MOVE_SET",
            setMoveLogId: moveLogId,
            toLocation: toLocation,

            fromLocation: fromLocationEq || setFromLocation,
            set: eq.set || setId,
            status: eq.status || "",
            deviceName: eq.deviceName || "",
            serialNumber: eq.serialNumber || "",
            base: baseEq,
            location: fromLocationEq, // snapshot old location
            equipmentId: equipmentId,
          });

          // GLOBAL equipment log
          const globalEqLogRef = doc(collection(db, "equipmentMoveLogs"));
          batch.set(globalEqLogRef, {
            movedAt: serverTimestamp(),
            movedAtClient: movedAtClient,
            movedBy: (currentUser && currentUser.displayName) || null,
            note: moveNote || "",
            reason: "MOVE_SET",
            setMoveLogId: moveLogId,
            toLocation: toLocation,

            fromLocation: fromLocationEq || setFromLocation,
            set: eq.set || setId,
            status: eq.status || "",
            deviceName: eq.deviceName || "",
            serialNumber: eq.serialNumber || "",
            base: baseEq,
            location: fromLocationEq,
            equipmentId: equipmentId,
          });
        });

        await batch.commit();
      }

      swal("Moved successfully", { icon: "success" });

      // Force query objects rebuild in this component (optional, helps some setups)
      setLogRefreshKey((k) => k + 1);

      closeMoveModal();
    } catch (err) {
      console.error(err);
      swal(String((err && err.message) || err), { icon: "error" });
    }
  };

  const toggle = () => setShowModal(!showModal);

  const handleChange = (name) => (e) => {
    inputRef.current[name] = e.target.value;
    setTouchedFields((prev) => new Set(prev).add(name));
  };

  const handleOnEdit = (id) => {
    const data = (equipmentsList || []).find((item) => item.id === id);
    setEdit(data || {});
    setShowModal(true);
  };

  const handleOnHide = () => {
    setShowModal(false);
    Object.keys(inputRef.current).forEach((key) => {
      inputRef.current[key] = null;
    });
    setTouchedFields(new Set());
  };

  const handleOnDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this file!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        await deleteDoc(doc(db, "equipments", id)).then(() =>
          swal("Poof! Your file has been deleted!", { icon: "success" }),
        );
      } else {
        swal("Canceled");
      }
    });
  };

  const onSearchChange = () => {
    setSearchItem(searchRef.current ? searchRef.current.value : "");
  };

  const onFieldChange = (key) => {
    setSearchField(key);
    setSearchItem("");
    if (searchRef.current) searchRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ref = doc(db, "equipments", edit.id);

    const formData = {
      ...edit,
      ...Object.fromEntries(
        Object.entries(inputRef.current).filter(([key]) =>
          touchedFields.has(key),
        ),
      ),
      updatedAt: new Date(),
    };

    await updateDoc(ref, formData);
    handleOnHide();
  };

  const createSleepSet = async () => {
    const setId = (newSetId || "").trim().toUpperCase();
    const baseLocation = (newSetBaseLocation || "").trim();
    const currentLocation = (newSetCurrentLocation || baseLocation).trim();

    if (!setId)
      return swal("Please enter Set ID (A, B, C...)", { icon: "warning" });
    if (!baseLocation)
      return swal("Please enter Base Location", { icon: "warning" });

    if (!/^[A-Z0-9_-]+$/.test(setId)) {
      return swal("Set ID should be A-Z / 0-9 / _ / - only", {
        icon: "warning",
      });
    }

    try {
      const ref = doc(db, "sleepSets", setId);
      const existed = await getDoc(ref);
      if (existed.exists()) {
        return swal('Set "' + setId + '" already exists', { icon: "error" });
      }

      await setDoc(ref, {
        setId: setId,
        baseLocation: baseLocation,
        currentLocation: currentLocation,
        createdAt: serverTimestamp(),
        createdBy: (currentUser && currentUser.displayName) || null,
        updatedAt: serverTimestamp(),
        updatedBy: (currentUser && currentUser.displayName) || null,
      });

      swal("Created Sleep Set successfully", { icon: "success" });
      setShowCreateSetModal(false);
      setNewSetId("");
      setNewSetBaseLocation("");
      setNewSetCurrentLocation("");
    } catch (err) {
      console.error(err);
      swal(String((err && err.message) || err), { icon: "error" });
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Equipment List" motherMenu="Equipments" />

      {/* Edit Equipment Modal */}
      <Modal
        className="fade bd-example-modal-lg"
        show={showModal}
        onHide={toggle}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Edit Equipment Datails</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EquipmentForm onChange={handleChange} initailValues={edit} />
        </Modal.Body>
        <Modal.Footer />
        <div className="d-flex justify-content-center mb-3">
          <Button className="sm" onClick={handleSubmit}>
            Save
          </Button>
          <Button className="sm ml-2" variant="light" onClick={handleOnHide}>
            Cancel
          </Button>
        </div>
      </Modal>

      {/* Info */}
      <div className="text-danger mb-5">
        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <p className="fw-bold mb-2">
              วิธีตั้ง Serial Number สำหรับอุปกรณ์ที่{" "}
              <span className="bg-dark text-white px-2 rounded">
                มี Serial Number อยู่แล้ว
              </span>
            </p>

            <p>ใช้หมายเลข Serial Number ของอุปกรณ์โดยตรง</p>
            <ul className="mb-2">
              <li>
                <strong>EEG</strong> → ใช้ Serial Number จากตัวเครื่อง EEG
                โดยตรง รูปแบบ:{" "}
                <span className="bg-dark text-white px-2 rounded">
                  1801-0XXX
                </span>
              </li>
              <li>
                <strong>BBOX</strong> → ใช้ Serial Number จากตัวอุปกรณ์ BBOX
                โดยตรง รูปแบบ:{" "}
                <span className="bg-dark text-white px-2 rounded">
                  2203-0XXX
                </span>
              </li>
              <li>
                <strong>Smart Pulz</strong> → ใช้ Serial Number จากตัวเครื่อง
                Smart Pulz โดยตรง รูปแบบ:{" "}
                <span className="bg-dark text-white px-2 rounded">
                  1802-0XXX
                </span>
              </li>
              <li>
                <strong>EEG Adaptor (Electrode Box)</strong> → ใช้ Serial Number
                จากตัวอุปกรณ์ EEG Adaptor โดยตรง รูปแบบ:{" "}
                <span className="bg-dark text-white px-2 rounded">
                  2401-0XXX
                </span>
              </li>
            </ul>

            <p className="mt-2 mb-0">
              ตัวอย่าง:
              <br />
              <span className="bg-dark text-white px-2 rounded">
                1801-0001
              </span>{" "}
              → Serial Number ของเครื่อง EEG หมายเลข 001
              <br />
              <span className="bg-dark text-white px-2 rounded">
                2203-0005
              </span>{" "}
              → Serial Number ของเครื่อง BBOX หมายเลข 005
              <br />
              <span className="bg-dark text-white px-2 rounded">
                1802-0777
              </span>{" "}
              → Serial Number ของเครื่อง Smart Pulz หมายเลข 777
              <br />
              <span className="bg-dark text-white px-2 rounded">
                2401-0002
              </span>{" "}
              → Serial Number ของเครื่อง EEG Adaptor หมายเลข 002
            </p>
          </div>

          <div className="col-12 col-lg-6">
            <p className="fw-bold mb-2">
              วิธีตั้ง Serial Number สำหรับอุปกรณ์สิ้นเปลืองที่{" "}
              <span className="bg-dark text-white px-2 rounded">
                ไม่มี Serial Number
              </span>
            </p>

            <p>
              ใช้รูปแบบ:{" "}
              <span className="bg-dark text-white px-2 rounded">
                EQUIPMENT-SET-XXX
              </span>
            </p>

            <ul className="mb-2">
              <li>
                <strong>EQUIPMENT</strong> = ชื่ออุปกรณ์ (ตัวพิมพ์ใหญ่ เช่น
                CAMERA, RIPBELT, THERMISTOR)
              </li>
              <li>
                <strong>SET</strong> = เซตตรวจ (เช่น A, B)
              </li>
              <li>
                <strong>XXX</strong> = หมายเลขอุปกรณ์ (เช่น 001, 002, 003)
              </li>
            </ul>

            <p className="mt-2 mb-0">
              ตัวอย่าง:
              <br />
              <span className="bg-dark text-white px-2 rounded">
                CAMERA-A-001
              </span>{" "}
              → อุปกรณ์ Camera เซต A หมายเลข 001
              <br />
              <span className="bg-dark text-white px-2 rounded">
                RIPBELT-B-002
              </span>{" "}
              → อุปกรณ์ Ripbelt เซต B หมายเลข 002
              <br />
              <span className="bg-dark text-white px-2 rounded">
                THERMISTOR-F-005
              </span>{" "}
              → อุปกรณ์ Thermistor sensor เซต F หมายเลข 005
            </p>
          </div>
        </div>
      </div>

      {/* Sleep Sets Section (Half Page) */}
      <div className="mb-4">
        <div className="form-head d-flex flex-column flex-md-row mb-3 justify-content-between align-items-md-end">
          <div className="d-flex align-items-start mb-2 mb-md-0">
            <Button
              className="btn btn-primary"
              onClick={() => setShowCreateSetModal(true)}
              title="Create Sleep Set"
            >
              + New Sleep Set
            </Button>
          </div>

          <div className="d-flex align-items-end">
            <div className="input-group" style={{ width: 240 }}>
              <input
                className="form-control"
                placeholder="Search Set (A,B,C...)"
                value={setSearch}
                onChange={(e) => setSetSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <Row>
          <Col lg={6} md={12}>
            <Card className="mb-0">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="fw-bold">Sleep Sets</div>
                <div>
                  <Button
                    size="sm"
                    variant="light"
                    className="mr-2"
                    disabled={setIsStart}
                    onClick={getSetPrev}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    disabled={setIsEnd}
                    onClick={getSetNext}
                  >
                    Next
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Set</th>
                      <th>Base Location</th>
                      <th>Current Location</th>
                      <th style={{ width: 140 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sleepSetsList && sleepSetsList.length ? (
                      sleepSetsList.map((s) => (
                        <tr key={s.id}>
                          <td className="fw-bold">{s.setId || s.id}</td>
                          <td>{s.baseLocation || "-"}</td>
                          <td>{s.currentLocation || "-"}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openMoveModal(s)}
                            >
                              Edit Location
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center py-4">
                          No sets found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="p-2 small text-muted">
                  Page {setCurrentPage} / {setTotalPages}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} md={12} className="mb-3 mb-lg-0">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="fw-bold">Sleep Set Logs</div>
                <div>
                  <Button
                    size="sm"
                    variant="light"
                    className="mr-2"
                    disabled={setLogIsStart}
                    onClick={getSetLogPrev}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    disabled={setLogIsEnd}
                    onClick={getSetLogNext}
                  >
                    Next
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Moved At</th>
                      <th>Set</th>
                      <th>From</th>
                      <th>To</th>
                      <th>By</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sleepSetLogs && sleepSetLogs.length ? (
                      sleepSetLogs.map((l) => (
                        <tr key={l.id}>
                          <td>{formatMovedAt(l.movedAtClient || l.movedAt)}</td>
                          <td className="fw-bold">{l.set || "-"}</td>
                          <td>{l.fromLocation || "-"}</td>
                          <td>{l.toLocation || "-"}</td>
                          <td>{l.movedBy || "-"}</td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: 160 }}
                          >
                            {l.note || "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          No logs
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="p-2 small text-muted">
                  Page {setLogCurrentPage} / {setLogTotalPages}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Move Modal */}
      {selectedSet && (
        <Modal show={showMoveModal} onHide={closeMoveModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              Move Set {selectedSet.setId || selectedSet.id}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="mb-2">
              <div className="small text-muted">Base Location</div>
              <div className="fw-bold">{selectedSet.baseLocation || "-"}</div>
            </div>

            <div className="mb-3">
              <label className="form-label">Current Location</label>
              <input
                className="form-control"
                value={moveToLocation}
                onChange={(e) => setMoveToLocation(e.target.value)}
                placeholder="Enter new location"
              />
            </div>

            <div>
              <label className="form-label">Note (optional)</label>
              <textarea
                className="form-control"
                rows={3}
                value={moveNote}
                onChange={(e) => setMoveNote(e.target.value)}
                placeholder="Reason / remark"
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" onClick={closeMoveModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={moveSleepSet}>
              Save & Log
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Equipments Toolbar */}
      <div className="form-head d-flex flex-column flex-lg-row mb-3 mb-lg-3 justify-content-between align-items-lg-end">
        <div className="d-flex align-items-start">
          <Link
            to="/equipment-register"
            className="btn btn-danger"
            data-toggle="tooltip"
            data-placement="top"
            title="Equipment Registration"
          >
            + New Equipment
          </Link>
        </div>

        <div className="d-flex align-items-end">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder={
                searchField === "serialNumber"
                  ? "Search serial number here"
                  : searchField === "deviceName"
                    ? "Search device name here"
                    : searchField === "set"
                      ? "Search Sleep Study Set here"
                      : "Search location here"
              }
              ref={searchRef}
              onChange={onSearchChange}
              value={searchItem}
            />
            <div className="input-group-append">
              <span
                className="input-group-text c-pointer"
                onClick={() => searchRef.current && searchRef.current.focus()}
              >
                <i className="flaticon-381-search-2"></i>
              </span>
            </div>
          </div>

          <Dropdown
            className="align-items-end d-inline-flex ml-2"
            onSelect={onFieldChange}
          >
            <Dropdown.Toggle variant="outline-primary" id="dropdown-search-by">
              <i className="flaticon-381-menu-1"></i>{" "}
              {searchField === "serialNumber"
                ? "Serial Number"
                : searchField === "deviceName"
                  ? "Device Name"
                  : searchField === "set"
                    ? "Sleep Study Set"
                    : "Location"}
            </Dropdown.Toggle>

            <Dropdown.Menu className="dropdown-menu-right">
              <Dropdown.Item href="#" eventKey="serialNumber">
                Serial Number
              </Dropdown.Item>
              <Dropdown.Item href="#" eventKey="deviceName">
                Device Name
              </Dropdown.Item>
              <Dropdown.Item href="#" eventKey="set">
                Sleep Study Set
              </Dropdown.Item>
              <Dropdown.Item href="#" eventKey="location">
                Location
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Equipments List */}
      <EquipmentItem
        equipments={equipmentsList}
        onEdit={handleOnEdit}
        onDelete={handleOnDelete}
        totalItems={totalItems}
        totalPages={totalPages}
        currentPage={currentPage}
        itemsPerPage={itemPerPage}
        goToPage={goToPage}
        isStart={isStart}
        isEnd={isEnd}
        getNext={getNext}
        getPrev={getPrev}
      />

      {/* Logs (2 sections) */}
      <Row className="mb-5">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Equipment Logs</div>
              <div>
                <Button
                  size="sm"
                  variant="light"
                  className="mr-2"
                  disabled={eqLogIsStart}
                  onClick={getEqLogPrev}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  disabled={eqLogIsEnd}
                  onClick={getEqLogNext}
                >
                  Next
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Moved At</th>
                    <th>Status</th>
                    <th>Serial</th>
                    <th>Device</th>
                    <th>Set</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {equipmentLogs && equipmentLogs.length ? (
                    equipmentLogs.map((l) => (
                      <tr key={l.id}>
                        <td>{formatMovedAt(l.movedAtClient || l.movedAt)}</td>
                        <td>{l.status || "-"}</td>
                        <td>{l.serialNumber || "-"}</td>
                        <td>{l.deviceName || "-"}</td>
                        <td className="fw-bold">{l.set || "-"}</td>
                        <td>{l.fromLocation || l.location || "-"}</td>
                        <td>{l.toLocation || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        No logs
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <div className="p-2 small text-muted">
                Page {eqLogCurrentPage} / {eqLogTotalPages}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Sleep Set Modal */}
      <Modal
        show={showCreateSetModal}
        onHide={() => setShowCreateSetModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Sleep Set</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Set ID (A, B, C...)</label>
            <input
              className="form-control"
              value={newSetId}
              onChange={(e) => setNewSetId(e.target.value)}
              placeholder="A"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Base Location</label>
            <input
              className="form-control"
              value={newSetBaseLocation}
              onChange={(e) => setNewSetBaseLocation(e.target.value)}
              placeholder="BDT"
            />
          </div>

          <div>
            <label className="form-label">Current Location (optional)</label>
            <input
              className="form-control"
              value={newSetCurrentLocation}
              onChange={(e) => setNewSetCurrentLocation(e.target.value)}
              placeholder="If empty, will use Base Location"
            />
            <div className="small text-muted mt-1">
              If you leave this blank, it will be the same as Base Location.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={() => setShowCreateSetModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createSleepSet}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default EquipmentList;
